import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Droplet, Users, Building2, LogOut, Loader2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

export default function Welcome() {
  const navigate = useNavigate();
  const { logout, user } = useAuth();
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  // If user is in branch_setup status, redirect to company page (which shows branches step)
  useEffect(() => {
    if (user?.onboardingStatus === 'branch_setup') {
      navigate('/company');
    }
  }, [user?.onboardingStatus, navigate]);

  const handleExit = async () => {
    console.log('[Welcome] Exit button clicked');
    setIsLoggingOut(true);
    try {
      await logout();
      console.log('[Welcome] Logout completed');
      // Wait a moment for logout to complete
      await new Promise(resolve => setTimeout(resolve, 300));
      console.log('[Welcome] Navigating to login');
      navigate('/login', { replace: true, state: { justLoggedOut: true } });
    } catch (err) {
      console.error('[Welcome] Exit error:', err);
      setIsLoggingOut(false);
    }
  };

  if (isLoggingOut) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-cyan-50 to-blue-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 text-cyan-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-cyan-50 to-blue-50 flex flex-col p-4">
      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center">
        <div className="w-full max-w-2xl">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="flex justify-center mb-6">
              <div className="bg-white rounded-full p-4 shadow-lg">
                <Droplet className="h-12 w-12 text-cyan-600" />
              </div>
            </div>
            <h1 className="text-4xl font-bold text-gray-900 mb-2">Welcome to AquaFlow</h1>
            <p className="text-lg text-gray-600">Let&apos;s get you set up</p>
          </div>

          {/* Onboarding Info */}
          <div className="bg-white rounded-2xl shadow-xl p-8 mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Getting Started</h2>
            <p className="text-gray-600 mb-6">
              Your account is ready! Now you need to set up your company. You can either join an existing company using an invite code, or create your own company and start managing your water service operations.
            </p>

            {/* Two Options */}
            <div className="space-y-4 mb-6">
              {/* Join Company Option */}
              <button
                onClick={() => navigate('/join')}
                className="w-full bg-white border-2 border-cyan-200 rounded-xl p-6 hover:bg-cyan-50 hover:border-cyan-400 transition-all text-left"
              >
                <div className="flex items-start gap-4">
                  <div className="bg-cyan-100 rounded-lg p-3 flex-shrink-0">
                    <Users className="h-6 w-6 text-cyan-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">Join Existing Company</h3>
                    <p className="text-gray-600 text-sm">
                      Have an invite code? Enter it here to join your team.
                    </p>
                  </div>
                  <div className="text-cyan-600 font-semibold">→</div>
                </div>
              </button>

              {/* Create Company Option */}
              <button
                onClick={() => navigate('/company')}
                className="w-full bg-gradient-to-r from-cyan-600 to-cyan-500 rounded-xl p-6 hover:shadow-lg hover:from-cyan-700 hover:to-cyan-600 transition-all text-left"
              >
                <div className="flex items-start gap-4">
                  <div className="bg-white bg-opacity-20 rounded-lg p-3 flex-shrink-0">
                    <Building2 className="h-6 w-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-white mb-1">Create New Company</h3>
                    <p className="text-cyan-50 text-sm">
                      Start fresh by creating your own company and service areas.
                    </p>
                  </div>
                  <div className="text-white font-semibold">→</div>
                </div>
              </button>
            </div>

            {/* Divider */}
            <div className="border-t border-gray-200 pt-6">
              {/* Footer text */}
              <p className="text-center text-gray-600 mb-4">
                You&apos;ll be able to add team members and configure branches after setup
              </p>

              {/* Exit Button */}
              <div className="flex justify-center">
                <button
                  onClick={handleExit}
                  disabled={isLoggingOut}
                  className="text-gray-500 hover:text-gray-700 disabled:text-gray-300 text-sm font-medium transition-colors underline"
                >
                  Exit
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
